/**
 * The item-proto field vocabulary: what a recipe is allowed to say.
 *
 * Every entry here is transcribed from `engine/src/proto.cc` (protoRead /
 * protoItemDataRead — the wire order is the read order, nothing else) and named
 * after `engine/src/proto_types.h`. The layout is declared rather than
 * discovered because every field in a .pro is fixed-width and positionally
 * addressed: a field's byte offset is the sum of the widths before it, and that
 * is what makes a one-field edit a four-byte edit.
 *
 * Three things are encoded per field:
 *   - `kind`   — how to read/write it (`i32` big-endian, or `u8`).
 *   - `edit`   — whether a recipe may target it. The line is drawn at bytes, not
 *                at subject matter. *Repointing* at art the install already has
 *                (`fid`, `inventoryFid`, `maleFid`, `femaleFid`,
 *                `animationCode`) ships nothing: the value is an index into a
 *                list the player's own files already declare, so a recipe can
 *                express it as honestly as it expresses `maxDamage`. *New* art
 *                is the HOSTED class, because that genuinely is bytes the
 *                document cannot carry — as is `sid`, which points at a compiled
 *                script. An index that names nothing on a given install renders
 *                as silent nothing (art.cc:427-430), so the failure mode of a
 *                wrong repoint is an invisible item, not a crash; `compile()`
 *                checks the index against the base and warns.
 *   - `range`  — the legal interval, enforced by `validate()`. Ranges come from
 *                the enum extents in proto_types.h where one exists, and
 *                otherwise from "what the engine will not divide by, index with,
 *                or overflow on".
 *
 * NOTE (engine bug, do not imitate): protoItemDataWrite (proto.cc:1782-1783)
 * writes weapon maxDamage *before* minDamage, while protoItemDataRead reads min
 * before max. The engine's own writer does not round-trip its own reader. Only
 * the READ order is the file format — that is the order the game loads, and it
 * is what this table encodes. `_proto_save_pid` is a mapper/debug path; nothing
 * in the shipping game re-writes a .pro.
 */

export type FieldKind = 'i32' | 'u8';

export interface FieldSpec {
  /** Dotted name as it appears in a recipe's `fields` object. */
  name: string;
  kind: FieldKind;
  /** Whether a recipe may set it. Read-only fields are still parsed and exposed. */
  edit: boolean;
  /** Inclusive legal interval for recipe-set values. */
  range?: [number, number];
  /** One-line meaning, surfaced by the editor UI and the MCP `get_item` tool. */
  doc: string;
  /** Why a read-only field is read-only — shown verbatim in validation errors. */
  why?: string;
}

const I32_MIN = -2147483648;
const I32_MAX = 2147483647;

/**
 * The largest fid `validate()` can accept base-independently. A fid packs
 * `(rotation << 28) | (objectType << 24) | (animType << 16) | (weaponCode << 12) | frmId`
 * (art.cc:1083-1093), and `gArtListDescriptions` (art.cc:79) has eleven lists,
 * so object type 10 is the last one that names a directory. What we would like
 * to enforce — type nibble 0..10, rotation/anim/weapon bits all zero — is not an
 * interval, and `range` is an interval, so this is the honest upper bound: it
 * admits every fid a well-formed item can hold and rejects the rotation bits.
 */
const FID_MAX = 0x0affffff;

/** proto_types.h ItemType. The index into ITEM_SUBTYPE_FIELDS. */
export const ITEM_TYPES = ['armor', 'container', 'drug', 'weapon', 'ammo', 'misc', 'key'] as const;
export type ItemTypeName = (typeof ITEM_TYPES)[number];

/** proto_types.h DamageType — also the index order of the armor DR/DT arrays. */
export const DAMAGE_TYPES = ['normal', 'laser', 'fire', 'plasma', 'electrical', 'emp', 'explosion'] as const;

/** proto_types.h CaliberType, 0..18. Names kept for the editor's dropdown. */
export const CALIBER_TYPES = [
  'none', 'rocket', 'flamethrowerFuel', 'cEnergyCell', 'dEnergyCell', 'cal223', 'mm5', 'cal40',
  'mm10', 'cal44', 'mm14', 'gauge12', 'mm9', 'bb', 'cal45', 'mm2', 'mm47Caseless', 'nhNeedler', 'cal762',
] as const;

/** proto_types.h MaterialType, 0..7. */
export const MATERIAL_TYPES = ['glass', 'metal', 'plastic', 'wood', 'dirt', 'stone', 'cement', 'leather'] as const;

const SCRIPT = 'points at a compiled script this recipe cannot carry — HOSTED package';
const IDENTITY = 'identity of the proto itself; changing it in place would silently repoint every reference';

/**
 * Common item header, in protoRead order (proto.cc:1675-1692). pid/messageId/fid
 * lead every proto regardless of object type; the rest is the OBJ_TYPE_ITEM arm.
 */
export const ITEM_HEADER_FIELDS: FieldSpec[] = [
  { name: 'pid', kind: 'i32', edit: false, doc: 'type<<24 | 1-based items.lst line', why: IDENTITY },
  { name: 'messageId', kind: 'i32', edit: false, doc: 'base entry number in pro_item.msg (+0 name, +1 description)', why: IDENTITY },
  // fid has no "none" sentinel worth offering: -1 leaves a dropped item live on
  // the map with nothing drawn for it, which is a bug report, not a mod.
  { name: 'fid', kind: 'i32', edit: true, range: [0, FID_MAX], doc: 'full fid of the on-the-ground look — object type 0 means line (fid & 0xfff) of art/items/items.lst; whether that line exists on a given install is compile()\'s check, not this range\'s' },
  { name: 'lightDistance', kind: 'i32', edit: true, range: [0, 8], doc: 'tiles the item lights when carried/dropped' },
  { name: 'lightIntensity', kind: 'i32', edit: true, range: [0, 65536], doc: 'light strength, 0..65536' },
  { name: 'flags', kind: 'i32', edit: false, doc: 'ProtoFlags bitfield', why: 'flag semantics are engine-wide; v1 keeps them out of reach of a text edit' },
  { name: 'extendedFlags', kind: 'i32', edit: false, doc: 'ProtoExtendedFlags bitfield (can-use, can-pickup, …)', why: 'same as flags' },
  { name: 'sid', kind: 'i32', edit: false, doc: 'script id', why: SCRIPT },
  { name: 'type', kind: 'i32', edit: false, range: [0, 6], doc: 'ItemType — selects the subdata block', why: 'changes the length and meaning of every byte after it; make a new item instead' },
  { name: 'material', kind: 'i32', edit: true, range: [0, 7], doc: 'MaterialType (glass..leather) — drives break/burn messages' },
  { name: 'size', kind: 'i32', edit: true, range: [0, 999], doc: 'inventory size' },
  // Negative weight is deliberate modding vocabulary, not an accident to
  // refuse: itemGetWeight and every carry total are plain signed-int sums
  // (item.cc:815, 991), so an item that *lightens* the pack — the classic
  // bag-of-holding trick — works in the engine as-is.
  { name: 'weight', kind: 'i32', edit: true, range: [-999999, 999999], doc: 'weight in pounds; negative lightens the carrier' },
  { name: 'cost', kind: 'i32', edit: true, range: [0, 999999], doc: 'base barter value' },
  // -1 is the engine's own "no inventory art" sentinel (proto.cc:393,
  // interface.cc:1941), so it is a legal value, not a mistake to refuse.
  { name: 'inventoryFid', kind: 'i32', edit: true, range: [-1, FID_MAX], doc: 'full fid of the inventory-slot picture — conventionally object type 7, i.e. line (fid & 0xfff) of art/inven/inven.lst; -1 for no inventory art. Per-install index validity is compile()\'s check, not this range\'s' },
  { name: 'soundId', kind: 'u8', edit: true, range: [0, 255], doc: 'pickup/drop sound letter code (ASCII byte)' },
];

const dr = (i: number): FieldSpec => ({
  name: `damageResistance.${DAMAGE_TYPES[i]}`,
  kind: 'i32',
  edit: true,
  range: [-100, 100],
  doc: `% ${DAMAGE_TYPES[i]} damage resistance`,
});
const dt = (i: number): FieldSpec => ({
  name: `damageThreshold.${DAMAGE_TYPES[i]}`,
  kind: 'i32',
  edit: true,
  range: [0, 100],
  doc: `flat ${DAMAGE_TYPES[i]} damage subtracted before resistance`,
});

/**
 * Per-subtype blocks, each in protoItemDataRead order (proto.cc:1563-1636).
 * Index by ItemType, i.e. ITEM_TYPES.indexOf(name).
 */
export const ITEM_SUBTYPE_FIELDS: Record<ItemTypeName, FieldSpec[]> = {
  armor: [
    { name: 'armorClass', kind: 'i32', edit: true, range: [0, 100], doc: 'AC bonus' },
    ...DAMAGE_TYPES.map((_, i) => dr(i)),
    ...DAMAGE_TYPES.map((_, i) => dt(i)),
    { name: 'perk', kind: 'i32', edit: true, range: [-1, 118], doc: 'perk granted while worn, -1 for none' },
    // Despite the names these are NOT fids: every consumer passes them as the
    // frmId argument to buildFid(OBJ_TYPE_CRITTER, …) (inventoryComputeCritterFid,
    // inventory.cc:1557-1595), so the stored value is a bare list index, and
    // 4095 is the 12-bit frmId ceiling. Vanilla .pro data nonetheless stores a
    // *full* critter fid here (Metal Armor carries 0x0100000E) — harmless only
    // because buildFidInternal masks frmId & 0xfff (art.cc:1093). Recipes speak
    // the bare index; the range refuses the redundant full-fid spelling so two
    // recipes cannot claim the same body in two encodings.
    { name: 'maleFid', kind: 'i32', edit: true, range: [-1, 4095], doc: '0-based line in art/critters/critters.lst — the whole body model while worn; NOT a fid despite the name; -1 keeps the wearer\'s own look' },
    { name: 'femaleFid', kind: 'i32', edit: true, range: [-1, 4095], doc: '0-based line in art/critters/critters.lst — the whole body model while worn; NOT a fid despite the name; -1 keeps the wearer\'s own look' },
  ],
  container: [
    { name: 'maxSize', kind: 'i32', edit: true, range: [0, 100000], doc: 'capacity in inventory size units' },
    { name: 'openFlags', kind: 'i32', edit: true, range: [0, 255], doc: 'container open/lock flags' },
  ],
  drug: [
    { name: 'stat.0', kind: 'i32', edit: true, range: [-2, 34], doc: 'first stat affected, -2 for unused' },
    { name: 'stat.1', kind: 'i32', edit: true, range: [-2, 34], doc: 'second stat affected' },
    { name: 'stat.2', kind: 'i32', edit: true, range: [-2, 34], doc: 'third stat affected' },
    { name: 'amount.0', kind: 'i32', edit: true, range: [-50, 50], doc: 'immediate change to stat.0' },
    { name: 'amount.1', kind: 'i32', edit: true, range: [-50, 50], doc: 'immediate change to stat.1' },
    { name: 'amount.2', kind: 'i32', edit: true, range: [-50, 50], doc: 'immediate change to stat.2' },
    { name: 'duration1', kind: 'i32', edit: true, range: [0, 100000], doc: 'ticks until the first delayed effect' },
    { name: 'amount1.0', kind: 'i32', edit: true, range: [-50, 50], doc: 'first delayed change to stat.0' },
    { name: 'amount1.1', kind: 'i32', edit: true, range: [-50, 50], doc: 'first delayed change to stat.1' },
    { name: 'amount1.2', kind: 'i32', edit: true, range: [-50, 50], doc: 'first delayed change to stat.2' },
    { name: 'duration2', kind: 'i32', edit: true, range: [0, 100000], doc: 'ticks until the second delayed effect' },
    { name: 'amount2.0', kind: 'i32', edit: true, range: [-50, 50], doc: 'second delayed change to stat.0' },
    { name: 'amount2.1', kind: 'i32', edit: true, range: [-50, 50], doc: 'second delayed change to stat.1' },
    { name: 'amount2.2', kind: 'i32', edit: true, range: [-50, 50], doc: 'second delayed change to stat.2' },
    { name: 'addictionChance', kind: 'i32', edit: true, range: [0, 100], doc: '% chance of addiction' },
    { name: 'withdrawalEffect', kind: 'i32', edit: true, range: [-1, 118], doc: 'perk applied on withdrawal, -1 for none' },
    { name: 'withdrawalOnset', kind: 'i32', edit: true, range: [0, 100000], doc: 'ticks before withdrawal begins' },
  ],
  weapon: [
    // Validated at use time, not at load time: a critter whose base art lacks
    // the FRMs for this set simply refuses the equip and logs it
    // (inventory.cc:3941-3947), so an unsupported code is a weapon nobody can
    // hold rather than a broken save.
    { name: 'animationCode', kind: 'i32', edit: true, range: [0, 15], doc: 'WeaponAnimation (art_defs.h:100-126) — which of the wielding critter\'s 16 hold/fire animation sets this weapon uses' },
    { name: 'minDamage', kind: 'i32', edit: true, range: [0, 9999], doc: 'minimum damage per hit' },
    { name: 'maxDamage', kind: 'i32', edit: true, range: [0, 9999], doc: 'maximum damage per hit' },
    { name: 'damageType', kind: 'i32', edit: true, range: [0, 6], doc: 'DamageType (normal..explosion)' },
    { name: 'maxRange1', kind: 'i32', edit: true, range: [0, 100], doc: 'range of primary attack mode, in tiles' },
    { name: 'maxRange2', kind: 'i32', edit: true, range: [0, 100], doc: 'range of secondary attack mode, in tiles' },
    { name: 'projectilePid', kind: 'i32', edit: true, range: [-1, I32_MAX], doc: 'pid of the projectile proto, -1 for none' },
    { name: 'minStrength', kind: 'i32', edit: true, range: [0, 20], doc: 'ST below which the wielder takes an accuracy penalty' },
    { name: 'actionPointCost1', kind: 'i32', edit: true, range: [0, 50], doc: 'AP cost of primary attack mode' },
    { name: 'actionPointCost2', kind: 'i32', edit: true, range: [0, 50], doc: 'AP cost of secondary attack mode' },
    { name: 'criticalFailureType', kind: 'i32', edit: true, range: [0, 6], doc: 'critical-failure table index' },
    { name: 'perk', kind: 'i32', edit: true, range: [-1, 118], doc: 'perk granted while wielded, -1 for none' },
    { name: 'rounds', kind: 'i32', edit: true, range: [0, 100], doc: 'rounds fired per burst' },
    { name: 'caliber', kind: 'i32', edit: true, range: [0, 18], doc: 'CaliberType this weapon chambers' },
    { name: 'ammoTypePid', kind: 'i32', edit: true, range: [-1, I32_MAX], doc: 'pid of the default ammo proto' },
    { name: 'ammoCapacity', kind: 'i32', edit: true, range: [0, 1000], doc: 'magazine size' },
    { name: 'soundCode', kind: 'u8', edit: true, range: [0, 255], doc: 'firing-sound letter code (ASCII byte)' },
  ],
  ammo: [
    { name: 'caliber', kind: 'i32', edit: true, range: [0, 18], doc: 'CaliberType' },
    { name: 'quantity', kind: 'i32', edit: true, range: [1, 1000], doc: 'rounds per box' },
    { name: 'armorClassModifier', kind: 'i32', edit: true, range: [-100, 100], doc: "change to target's AC" },
    { name: 'damageResistanceModifier', kind: 'i32', edit: true, range: [-100, 100], doc: "change to target's DR" },
    { name: 'damageMultiplier', kind: 'i32', edit: true, range: [1, 100], doc: 'damage numerator' },
    { name: 'damageDivisor', kind: 'i32', edit: true, range: [1, 100], doc: 'damage denominator — never 0, the engine divides by it' },
  ],
  misc: [
    { name: 'powerTypePid', kind: 'i32', edit: true, range: [-1, I32_MAX], doc: 'pid of the item that recharges this one' },
    { name: 'powerType', kind: 'i32', edit: true, range: [-1, 18], doc: 'caliber consumed on use' },
    { name: 'charges', kind: 'i32', edit: true, range: [0, 100000], doc: 'starting charges' },
  ],
  key: [{ name: 'keyCode', kind: 'i32', edit: true, range: [I32_MIN, I32_MAX], doc: 'key code matched against door protos' }],
};

export function fieldWidth(kind: FieldKind): number {
  return kind === 'u8' ? 1 : 4;
}

/** Full ordered layout for an item of the given subtype: header then subdata. */
export function itemLayout(type: ItemTypeName): FieldSpec[] {
  return [...ITEM_HEADER_FIELDS, ...ITEM_SUBTYPE_FIELDS[type]];
}

/** Serialized length of a well-formed item .pro of the given subtype. */
export function itemProtoSize(type: ItemTypeName): number {
  return itemLayout(type).reduce((n, f) => n + fieldWidth(f.kind), 0);
}
